import { useCallback, useEffect, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import {
  checkDetachAvailability,
  decideOnDetachFromParents,
  getDetachRequests,
  requestDetachFromParents,
  updateUserPreferences,
} from "src/app/utils/backendEndpointCalls.util";

interface DetachRequestParent {
  fullName: string;
}

interface DetachRequestModel {
  id: string;
  birthdate: string;
  fullName: string;
}

export interface DetachRequestItem {
  model: DetachRequestModel;
  parents: DetachRequestParent[];
}

const SettingsScreenViewModel = () => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [role, setRole] = useState<string>("");

  const [isCheckingDetachAvailability, setIsCheckingDetachAvailability] =
    useState<boolean>(false);
  const [isDetachAvailable, setIsDetachAvailable] = useState<boolean>(false);
  const [isRequestingDetach, setIsRequestingDetach] = useState<boolean>(false);

  const [detachRequests, setDetachRequests] = useState<DetachRequestItem[]>([]);
  const [isLoadingDetachRequests, setIsLoadingDetachRequests] =
    useState<boolean>(false);
  const [processingDetachRequestId, setProcessingDetachRequestId] = useState<
    string | null
  >(null);

  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);

  const [separatePagesPreference, setSeparatePagesPreference] =
    useState<boolean>(false);

  const [shareWithChildrenPreference, setShareWithChildrenPreference] =
    useState<boolean>(false);

  const onTabChanged = (_: React.SyntheticEvent, value: number) => {
    setSelectedTab(value);
  };

  const showError = (message: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const showSuccess = (message: string) => {
    setIsSnackbarError(false);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const loadDetachAvailability = useCallback(async () => {
    setIsCheckingDetachAvailability(true);
    const response = await checkDetachAvailability();
    setIsCheckingDetachAvailability(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setIsDetachAvailable(Boolean(response.available));
  }, []);

  const loadDetachRequests = useCallback(async () => {
    setIsLoadingDetachRequests(true);
    const response = await getDetachRequests();
    setIsLoadingDetachRequests(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setDetachRequests(response.requests ?? []);
  }, []);

  useEffect(() => {
    const storedRole = localStorage.getItem(localStorageConstants.ROLE) ?? "";
    setRole(storedRole);
    const selectedSeparatePagesPreference: string | null = localStorage.getItem(
      localStorageConstants.SEPARATE_PAGES_PREFERENCE,
    );
    setSeparatePagesPreference(selectedSeparatePagesPreference === "true");
    const selectedShareWithChildrenPreference: string | null =
      localStorage.getItem(localStorageConstants.SHARE_WITH_CHILDREN);
    setShareWithChildrenPreference(
      selectedShareWithChildrenPreference === "true",
    );
  }, []);

  useEffect(() => {
    if (role === "Model") {
      loadDetachAvailability();
      return;
    }

    if (role === "Admin") {
      loadDetachRequests();
    }
  }, [loadDetachAvailability, loadDetachRequests, role]);

  const onRequestDetachPressed = async () => {
    setIsRequestingDetach(true);
    const response = await requestDetachFromParents();
    setIsRequestingDetach(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setIsDetachAvailable(false);
    showSuccess("Detach request submitted successfully.");
  };

  const onDetachDecision = async (
    request: DetachRequestItem,
    detach: boolean,
  ) => {
    setProcessingDetachRequestId(request.model.id);

    const response = await decideOnDetachFromParents(detach, request.model.id);

    setProcessingDetachRequestId(null);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setDetachRequests((prev) =>
      prev.filter((item) => item.model.id !== request.model.id),
    );

    showSuccess(detach ? "User detached successfully." : "Request dismissed.");
  };

  const onDetachPressed = async (request: DetachRequestItem) => {
    await onDetachDecision(request, true);
  };

  const onDismissPressed = async (request: DetachRequestItem) => {
    await onDetachDecision(request, false);
  };

  const onSeparatePagesPreferenceChanged = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue: boolean = event.target.checked;
    setSeparatePagesPreference(newValue);
    localStorage.setItem(
      localStorageConstants.SEPARATE_PAGES_PREFERENCE,
      newValue.toString(),
    );
    await updateUserPreferences({ separateSites: newValue });
  };

  const onShareWithChildrenPreferenceChanged = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue: boolean = event.target.checked;
    setShareWithChildrenPreference(newValue);
    localStorage.setItem(
      localStorageConstants.SHARE_WITH_CHILDREN,
      newValue.toString(),
    );
    await updateUserPreferences({ shareFilesWithChildren: newValue });
  };

  return {
    selectedTab,
    onTabChanged,
    role,
    isCheckingDetachAvailability,
    isDetachAvailable,
    isRequestingDetach,
    onRequestDetachPressed,
    detachRequests,
    isLoadingDetachRequests,
    processingDetachRequestId,
    onDetachPressed,
    onDismissPressed,
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    separatePagesPreference,
    onSeparatePagesPreferenceChanged,
    shareWithChildrenPreference,
    onShareWithChildrenPreferenceChanged,
  };
};

export default SettingsScreenViewModel;
